import { useState, useEffect, useMemo } from 'react'
import { useAppContext } from '../../context/AppContext'
import MarkdownRenderer from '../common/MarkdownRenderer/MarkdownRenderer'
import './MessageBubble.css'
import ReferencePanel from './ReferencePanel'

const PREFERRED_LLM_STORAGE_KEY = 'preferred_llm_config'

const MessageBubble = ({
  message,
  isNewMessage = false,
  onExtendQuery,
  isStreaming = false,
  agentSteps = []
}) => {
  const { state, defaultModelFor } = useAppContext()
  const [displayedContent, setDisplayedContent] = useState('')
  const [isTypingComplete, setIsTypingComplete] = useState(false)
  const [selectedReference, setSelectedReference] = useState(null)
  const [referencesCollapsed, setReferencesCollapsed] = useState(true)
  const [isExtending, setIsExtending] = useState(false)
  const [showLLMSelector, setShowLLMSelector] = useState(false)
  const [availableLLMs, setAvailableLLMs] = useState([])
  const [autoSelectedModel, setAutoSelectedModel] = useState(null)
  const [expandedProviders, setExpandedProviders] = useState({ openai: false, anthropic: false })
  const [preferredLLM, setPreferredLLM] = useState(null)
  const [reasoningCollapsed, setReasoningCollapsed] = useState(true)

  const { cleanText, contextStatus } = useMemo(() => {
    const raw = message.content || ''
    const contextMatch = raw.match(/Found in Context\?\s*(Yes|No)/i)
    const status = contextMatch ? contextMatch[1].toLowerCase() : null
    const cleaned = message.type === 'assistant'
      ? raw.replace(/CONTEXT EVALUATION:[\s\S]*?RESPONSE:\s*/i, '').trim()
      : raw
    return { cleanText: cleaned, contextStatus: status }
  }, [message.content, message.type])

  // Helper function to detect provider from model name
  const detectProviderFromModel = (model) => {
    if (!model) return null
    const modelLower = model.toLowerCase()
    
    // OpenAI models
    if (modelLower.includes('gpt-') || modelLower.includes('gpt4') || modelLower.includes('o1-')) {
      return 'openai'
    }
    // Anthropic models
    if (modelLower.includes('claude')) {
      return 'anthropic'
    }
    // Google models
    if (modelLower.includes('gemini') || modelLower.includes('palm')) {
      return 'google'
    }
    // Ollama/local models (usually have specific patterns)
    if (modelLower.includes('llama') || modelLower.includes('mistral') || modelLower.includes('codellama')) {
      return 'ollama'
    }
    
    return null
  }

  // Helper function to get display name for provider
  const getProviderDisplayName = (provider) => {
    const providerNames = {
      'openai': 'OpenAI',
      'anthropic': 'Anthropic',
      'google': 'Google',
      'ollama': 'Ollama'
    }
    return providerNames[provider] || provider
  }

  // Helper function to get icon for provider
  const getProviderIcon = (provider) => {
    const providerIcons = {
      'openai': 'fas fa-robot',
      'anthropic': 'fas fa-brain',
      'google': 'fab fa-google',
      'ollama': 'fas fa-server'
    }
    return providerIcons[provider] || 'fas fa-cog'
  }

  const getContextProviderLabel = (source) => {
    if (source.provider === 'clabs') return 'Clabs Knowledge Store'
    if (source.provider === 'rag') return 'RAG Documents'
    return 'Internal Memory'
  }

  // Function to automatically select the best model based on auto mode rules
  const selectBestModelForQuery = (query) => {
    if (!state.autoModeEnabled || !state.autoModeRules || state.autoModeRules.length === 0) {
      return null
    }

    const queryLower = query.toLowerCase()
    let bestMatch = null
    let highestScore = 0

    // Score each rule based on keyword matches
    for (const rule of state.autoModeRules) {
      if (!rule.enabled) continue

      let score = 0
      const keywords = rule.keywords || []

      // Count keyword matches
      for (const keyword of keywords) {
        if (queryLower.includes(keyword.toLowerCase())) {
          score += 1
        }
      }

      // If this rule has more matches than the current best, use it
      if (score > highestScore) {
        highestScore = score
        
        // Validate provider/model combination - detect actual provider from model name
        const detectedProvider = detectProviderFromModel(rule.model)
        const actualProvider = detectedProvider || rule.provider
        
        // Log warning if there's a mismatch
        if (detectedProvider && detectedProvider !== rule.provider) {
          console.warn(`Auto Mode Rule "${rule.name}" has mismatched provider/model: provider="${rule.provider}" but model="${rule.model}" belongs to "${detectedProvider}". Using detected provider.`)
        }
        
        bestMatch = {
          id: actualProvider,
          name: getProviderDisplayName(actualProvider),
          model: rule.model,
          icon: getProviderIcon(actualProvider),
          ruleName: rule.name,
          score: score
        }
      }
    }

    // If no keywords matched, use the general Q&A rule as fallback
    if (highestScore === 0) {
      const fallbackRule = state.autoModeRules.find(rule => 
        rule.enabled && (rule.id === 'general-qa' || rule.name.toLowerCase().includes('general'))
      )
      
      if (fallbackRule) {
        // Validate provider/model combination for fallback rule too
        const detectedProvider = detectProviderFromModel(fallbackRule.model)
        const actualProvider = detectedProvider || fallbackRule.provider
        
        if (detectedProvider && detectedProvider !== fallbackRule.provider) {
          console.warn(`Auto Mode Fallback Rule "${fallbackRule.name}" has mismatched provider/model: provider="${fallbackRule.provider}" but model="${fallbackRule.model}" belongs to "${detectedProvider}". Using detected provider.`)
        }
        
        bestMatch = {
          id: actualProvider,
          name: getProviderDisplayName(actualProvider),
          model: fallbackRule.model,
          icon: getProviderIcon(actualProvider),
          ruleName: fallbackRule.name,
          score: 0
        }
      }
    }

    // console.log('Auto Mode Model Selection:', {
    //   query: query.substring(0, 50) + '...',
    //   bestMatch,
    //   highestScore,
    //   autoModeEnabled: state.autoModeEnabled,
    //   rulesCount: state.autoModeRules.length
    // })

    return bestMatch
  }

  // Typing effect for AI messages (only for new messages)
  useEffect(() => {
    if (message.type === 'assistant' && !message.isError && isNewMessage) {
      setDisplayedContent('')
      setIsTypingComplete(false)
      
      const content = cleanText
      let currentIndex = 0
      
      const typeNextCharacter = () => {
        if (currentIndex < content.length) {
          // Type 1-3 characters at once for faster display
          const charsToAdd = Math.min(
            Math.floor(Math.random() * 2) + 1, // 1-2 characters
            content.length - currentIndex
          )
          currentIndex += charsToAdd
          setDisplayedContent(content.slice(0, currentIndex))
          
          // Variable typing speed for more natural feel - faster speed
          const delay = Math.random() * 8 + 5
          setTimeout(typeNextCharacter, delay)
        } else {
          setIsTypingComplete(true)
        }
      }
      
      // Start typing after a brief delay - reduced for faster start
      const startDelay = setTimeout(typeNextCharacter, 100)
      
      return () => {
        clearTimeout(startDelay)
        setDisplayedContent(content)
        setIsTypingComplete(true)
      }
    } else {
      // For user messages, error messages, or existing assistant messages, show immediately
      setDisplayedContent(cleanText)
      setIsTypingComplete(true)
    }
  }, [cleanText, message.type, message.isError, isNewMessage])

  // Load available LLMs from configuration
  useEffect(() => {
    const loadAvailableLLMs = () => {
      try {
        console.log('MessageBubble: Loading available LLM models', { allowUserModelSelection: state.allowUserModelSelection })
        const systemProviders = state.systemProviders || {}
        
        // If user model selection is enabled, show all individual models
        if (state.allowUserModelSelection) {
          const allModels = Object.entries(systemProviders).flatMap(([providerId, provider]) =>
            (provider?.models || []).map((model) => ({
              id: providerId,
              name: model,
              icon: getProviderIcon(providerId),
              model
            }))
          )
          console.log('MessageBubble: Loaded all individual models:', allModels.length)
          setAvailableLLMs(allModels)
        } else {
          // If user model selection is disabled, only show provider defaults
          const defaultProviders = Object.entries(systemProviders)
            .map(([providerId, provider]) => {
              const model = state.agentModels?.[providerId] || provider?.models?.[0]
              if (!model) return null
              return {
                id: providerId,
                name: provider?.name || getProviderDisplayName(providerId),
                icon: getProviderIcon(providerId),
                model
              }
            })
            .filter(Boolean)
          console.log('MessageBubble: Using default providers:', defaultProviders.length)
          setAvailableLLMs(defaultProviders)
        }
      } catch (error) {
        console.error('MessageBubble: Error loading available LLMs:', error)
        // Even on error, provide default providers
        setAvailableLLMs([
          { id: 'openai', name: 'OpenAI GPT', icon: 'fas fa-robot', model: state.agentModels?.openai || defaultModelFor('openai', 'chat') || '' }
        ])
      }
    }
    
    loadAvailableLLMs()
  }, [state.allowUserModelSelection, state.agentModels, state.systemProviders, defaultModelFor])

  useEffect(() => {
    try {
      const storedPreference = localStorage.getItem(PREFERRED_LLM_STORAGE_KEY)
      if (storedPreference) {
        const parsed = JSON.parse(storedPreference)
        if (parsed?.model) {
          setPreferredLLM(parsed)
        }
      }
    } catch (error) {
      console.warn('MessageBubble: Unable to load preferred LLM config', error)
    }
  }, [])

  useEffect(() => {
    if (!preferredLLM) return
    const hasMatch = availableLLMs.some(
      llm => llm.model === preferredLLM.model && llm.id === preferredLLM.id
    )
    if (!hasMatch) {
      setPreferredLLM(null)
      try {
        localStorage.removeItem(PREFERRED_LLM_STORAGE_KEY)
      } catch (error) {
        console.warn('MessageBubble: Unable to clear preferred LLM config', error)
      }
    }
  }, [availableLLMs, preferredLLM])

  useEffect(() => {
    setReasoningCollapsed(true)
  }, [message.id])

  const savePreferredLLM = (llm) => {
    try {
      localStorage.setItem(PREFERRED_LLM_STORAGE_KEY, JSON.stringify(llm))
      setPreferredLLM(llm)
    } catch (error) {
      console.warn('MessageBubble: Unable to save preferred LLM config', error)
    }
  }

    const formatTime = (timestamp) => {
    try {
      // Handle different timestamp formats
      let date
      if (timestamp instanceof Date) {
        date = timestamp
} else if (typeof timestamp === 'string') {
        date = new Date(timestamp)
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp)
      } else {
        // Fallback to current time if timestamp is invalid
        date = new Date()
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return new Date().toLocaleDateString()
      }
      
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date)
    } catch (error) {
      console.error('Error formatting timestamp:', error, timestamp)
      return new Date().toLocaleDateString()
    }
  }

  const handleReferenceClick = (source) => {
    setSelectedReference(selectedReference?.memory_id === source.memory_id ? null : source)
  }

  const toggleReferencesCollapse = () => {
    const nextCollapsed = !referencesCollapsed
    setReferencesCollapsed(nextCollapsed)
    // Close any expanded reference when collapsing the section
    if (nextCollapsed) {
      setSelectedReference(null)
    } else {
      // Enforce one-open-at-a-time behavior across footer panels
      setShowLLMSelector(false)
      setReasoningCollapsed(true)
    }
  }

  const toggleProviderExpansion = (providerId) => {
    setExpandedProviders(prev => ({
      ...prev,
      [providerId]: !prev[providerId]
    }))
  }

  const handleExtendQuery = async (selectedLLM = null) => {
    console.log('🎯 MessageBubble: handleExtendQuery called', { selectedLLM, availableLLMs: availableLLMs.length, autoModeEnabled: state.autoModeEnabled })
    
    if (!onExtendQuery || isExtending) {
      console.log('Cannot extend: onExtendQuery missing or already extending')
      return
    }

    // Extract the original query to use for auto mode selection
    let originalQuery = message.metadata?.originalQuery
    if (!originalQuery) {
      // Try to find the previous user message to get the query
      const messageElements = document.querySelectorAll('.message-wrapper.user')
      if (messageElements.length > 0) {
        const lastUserMessage = messageElements[messageElements.length - 1]
        const content = lastUserMessage.querySelector('.message-content')
        originalQuery = content?.textContent?.trim() || 'Please provide more information'
      } else {
        originalQuery = 'Please provide more information'
      }
    }

    const preferredIsAvailable = preferredLLM
      && availableLLMs.some(llm => llm.model === preferredLLM.model && llm.id === preferredLLM.id)

    if (!selectedLLM && preferredIsAvailable) {
      selectedLLM = preferredLLM
    }

    // If no LLM selected, no valid preference, and multiple models available, show selector
    if (!selectedLLM && !preferredIsAvailable && availableLLMs.length > 1) {
      console.log('🔽 No preferred LLM, showing selector for', availableLLMs.length, 'options')
      setShowLLMSelector(true)
      return
    }

    // Auto Mode: automatically select the best model based on query content
    if (state.autoModeEnabled && !selectedLLM) {
      const autoSelectedLLM = selectBestModelForQuery(originalQuery)
      if (autoSelectedLLM) {
        console.log('Auto Mode: Selected model automatically:', autoSelectedLLM)
        selectedLLM = autoSelectedLLM
        setAutoSelectedModel(autoSelectedLLM)
      }
    }
    
    // If we only have one LLM available and none selected, use it automatically
    if (!selectedLLM && availableLLMs.length === 1) {
      console.log('🎯 Only one LLM available, using automatically:', availableLLMs[0])
      selectedLLM = availableLLMs[0]
    }
    
    setIsExtending(true)
    setShowLLMSelector(false)
    
    try {
      // Get the original memory ID from the message metadata
      const originalMemoryId = message.metadata?.memoryId || message.metadata?.originalMemoryId
      
      console.log('Message metadata for memory ID:', {
        memoryId: message.metadata?.memoryId,
        originalMemoryId: message.metadata?.originalMemoryId,
        finalMemoryId: originalMemoryId,
        needsExtension: message.metadata?.needsExtension
      })
      
      // Use selected LLM or first available one
      const llmToUse = selectedLLM || availableLLMs[0]
      console.log('Calling onExtendQuery with:', { originalQuery, llmToUse, originalMemoryId })
      await onExtendQuery(originalQuery, llmToUse, originalMemoryId)
    } catch (error) {
      console.error('Error extending query:', error)
    } finally {
      setIsExtending(false)
    }
  }

  // Check if LLM extension is available - more flexible detection
  const hasExplicitMarker = message.content && message.content.includes('**LLM_EXTENSION_AVAILABLE:** true')
  const hasNoContextPhrases = message.content && (
    message.content.includes('I cannot provide a reliable answer based on stored records') ||
    message.content.includes('I do not have sufficient information in memory') ||
    message.content.includes('no relevant information') ||
    message.content.includes('cannot address any aspects') ||
    message.content.includes('I cannot provide a response based on previous interactions') ||
    message.content.includes('no specific information about') ||
    message.content.includes('I do not have any specific information') ||
    message.content.includes('there is no relevant information') ||
    message.content.includes('cannot provide a response based on stored') ||
    message.content.includes('I cannot provide a response based on previous records') ||
    message.content.includes('No relevant information found in my memory database') ||
    message.content.includes('I would need you to provide the information') ||
    message.content.includes('I do not have specific information about') ||
    message.content.includes('I do not have sufficient information in memory about') ||
    message.content.includes("I don't have sufficient information in memory about") ||
    message.content.includes("don't have sufficient information in memory about") ||
    message.content.includes('cannot provide a reliable answer based on stored records')
  )
  
  // Allow extension when supervisor is used and indicates lack of useful information
  // even if some context was retrieved (but wasn't useful)
  const isSupervisorResponse = message.metadata?.supervisorUsed
  
  // Check if this is already an extended LLM response
  const isAlreadyExtendedQuery = message.metadata?.extendedQuery || message.metadata?.knowledgeBased
  
  // Check if this is a protected topic response (should not allow extension)
  const isProtectedTopic = message.metadata?.protection_status === "protected"
  
  // Check if the content contains the private response marker
  const isPrivateResponse = message.content?.includes("PRIVATE ANSWER")
  
  // Always allow extension for supervisor responses (backend sets needsExtension = true by default)
  // This gives users the flexibility to extend to LLM regardless of context found
  // BUT only if it's NOT already an extended LLM response AND NOT a protected topic
  // AND NOT a private response (additional safety check)
  const hasNeedsExtensionFlag = message.metadata?.needsExtension === true
  const isLLMExtensionAvailable = !isAlreadyExtendedQuery && !isProtectedTopic && !isPrivateResponse && (
    hasExplicitMarker || 
    hasNeedsExtensionFlag ||  // Backend always sets this to true for supervisor responses
    (isSupervisorResponse) ||  // Always allow extension for supervisor responses
    (hasNoContextPhrases && (isSupervisorResponse || message.type === 'assistant'))
  )

  const hasSources = (message.metadata?.contextSources?.length || 0) > 0

  const llmIndicatorRaw = autoSelectedModel?.name
    || message.metadata?.llmModel
    || message.metadata?.selectedModel?.model
    || message.metadata?.supervisorModel?.model
    || 'Extend'
  const llmIndicatorLabel = Array.isArray(llmIndicatorRaw) ? llmIndicatorRaw.join(', ') : llmIndicatorRaw

  const handleFooterExtendClick = () => {
    if (showLLMSelector) {
      setShowLLMSelector(false)
      return
    }

    const preferredIsAvailable = preferredLLM
      && availableLLMs.some(llm => llm.model === preferredLLM.model && llm.id === preferredLLM.id)

    // If no valid preference and multiple models available, show selector
    if (!preferredIsAvailable && availableLLMs.length > 1) {
      // Enforce one-open-at-a-time behavior across footer panels
      setReferencesCollapsed(true)
      setReasoningCollapsed(true)
      setShowLLMSelector(true)
      return
    }

    handleExtendQuery()
  }

  const handleToggleLLMSelector = () => {
    setShowLLMSelector(prev => {
      const next = !prev
      if (next) {
        // Enforce one-open-at-a-time behavior across footer panels
        setReferencesCollapsed(true)
        setReasoningCollapsed(true)
      }
      return next
    })
  }

  const toggleReasoningPanel = () => {
    if (!message.metadata?.agentSteps?.length) return
    setReasoningCollapsed(prev => {
      const next = !prev
      if (!next) {
        // Enforce one-open-at-a-time behavior across footer panels
        setShowLLMSelector(false)
        setReferencesCollapsed(true)
      }
      return next
    })
  }

  const handleSelectLLM = (llm) => {
    savePreferredLLM(llm)
    handleExtendQuery(llm)
  }

  // Build comparison option strictly from providers/models visible in this chat dropdown.
  // This avoids selecting hidden/unconfigured providers (e.g., google/mock) by accident.
  const visibleProviderIds = Array.from(
    new Set(
      availableLLMs
        .map((llm) => llm.id)
        .filter((provider) => provider && provider !== 'mock')
    )
  )

  const configuredComparisonLLMs = visibleProviderIds
    .map((provider) => {
      const providerOptions = availableLLMs.filter((llm) => llm.id === provider)
      if (providerOptions.length === 0) return null

      const configuredModel = state.agentModels?.[provider]
      const hasConfiguredVisible = configuredModel && providerOptions.some((llm) => llm.model === configuredModel)
      const model = hasConfiguredVisible ? configuredModel : providerOptions[0].model
      if (!model) return null

      return {
        id: provider,
        provider,
        model,
        name: getProviderDisplayName(provider)
      }
    })
    .filter(Boolean)

  const summaryProvider = state.summaryConfig?.provider || 'ollama'   // default when Organization LLM summarization not configured
  const summaryModel = state.summaryConfig?.model || defaultModelFor('ollama', 'classifier') || defaultModelFor('ollama', 'chat') || ''
  const comparisonSummaryLLM = {
    id: 'summary',
    provider: summaryProvider,
    model: summaryModel,
    name: 'Summary',
    summary: true
  }

  const comparisonOption = {
    id: 'comparison',
    name: 'Compare different LLMs',
    icon: 'fas fa-code-compare',
    model: 'comparison',
    llms: [...configuredComparisonLLMs, comparisonSummaryLLM]
  }

  const handleCopyResponse = async () => {
    const textToCopy = cleanText || message.content || ''
    if (!textToCopy) return

    try {
      await navigator.clipboard.writeText(textToCopy)
    } catch (error) {
      const textarea = document.createElement('textarea')
      textarea.value = textToCopy
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
  }
  
  // Debug logging
  // if (message.type === 'assistant') {
  //   console.group('LLM Extension Debug - Message ID:', message.id)
  //   console.log('Content preview:', message.content?.substring(0, 150) + '...')
  //   console.log('🏷️ Has explicit marker:', hasExplicitMarker)
  //   console.log('💬 Has no-context phrases:', hasNoContextPhrases)
  //   console.log('👨‍💼 Is supervisor response:', isSupervisorResponse)
  //   console.log('🛡️ Is protected topic:', isProtectedTopic)
  //   console.log('🔒 Is private response:', isPrivateResponse)
  //   console.log('⚙️ Metadata:', {
  //     supervisorUsed: message.metadata?.supervisorUsed,
  //     contextRetrieved: message.metadata?.contextRetrieved,
  //     protection_status: message.metadata?.protection_status
  //   })
  //   console.log('🎯 Extension available:', isLLMExtensionAvailable)
  //   console.log('Available LLMs:', availableLLMs.length)
  //   console.log('Full message metadata:', message.metadata)
  //   console.log('Message content contains no-context phrases:', hasNoContextPhrases)
  //   console.log('Is already extended query:', isAlreadyExtendedQuery)
  //   console.log('LLM provider:', message.metadata?.llmProvider)
  //   console.log('LLM model:', message.metadata?.llmModel)
  //   console.groupEnd()
  // }

  const formatReferenceTime = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown time'
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return 'Invalid date'
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch (error) {
      return 'Unknown time'
    }
  }

  // Helper function to analyze context sources by provider
  const analyzeContextSources = (sources) => {
    if (!sources || !Array.isArray(sources)) {
      return {
        hasMemorySources: false,
        hasClabsSources: false,
        hasRagSources: false,
        memorySources: [],
        clabsSources: [],
        ragSources: [],
        totalSources: 0
      }
    }

    const memorySources = sources.filter(source => 
      source.provider === 'memory' || 
      source.source_type === 'internal_memory' || 
      source.source_type === 'bedrock_knowledge'
    )
    
    const clabsSources = sources.filter(source =>
      source.provider === 'clabs' ||
      source.source_type === 'clabs'
    )

    const ragSources = sources.filter(source =>
      source.provider === 'rag' ||
      source.source_type === 'rag_document'
    )

    return {
      hasMemorySources: memorySources.length > 0,
      hasClabsSources: clabsSources.length > 0,
      hasRagSources: ragSources.length > 0,
      memorySources,
      clabsSources,
      ragSources,
      totalSources: sources.length
    }
  }

  // Analyze current message context sources
  const contextAnalysis = analyzeContextSources(message.metadata?.contextSources)

  return (
    <div className={`message-wrapper ${message.type} fade-in`}>
      <div className={`message-bubble ${message.type} ${message.isError ? 'error' : ''}`}>
        {message.type === 'user' ? (
          <div className="message-content ai-response">
            {message.content}
          </div>
        ) : (
          <div className="assistant-message-layout">
            <div className="assistant-content-column">
              {/* 1. Show live stack during streaming */}
              {isStreaming && !displayedContent && (
                <div className={`message-content ai-response ${isPrivateResponse ? 'private-response' : ''}`}>
                  <div className="agent-reasoning-stack">
                    {agentSteps.length === 0 && (
                      <div className="agent-step active">
                        <span className="step-bullet">›</span>
                        <span>Initializing...</span>
                      </div>
                    )}
                    {agentSteps.map((step, index) => {
                      const isLast = index === agentSteps.length - 1
                      return (
                        <div key={index} className={`agent-step ${isLast ? 'active' : 'completed'}`}>
                          <span className="step-bullet">›</span>
                          <span>{step.message}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* 2. Show final content and collapsed reasoning once streaming yields text */}
              {(!isStreaming || displayedContent) && (
                <>
                  <div
                    className={`message-content ai-response ${isPrivateResponse ? 'private-response' : ''}`}
                    style={{ position: 'relative' }}
                  >
                    {/* Invisible full content to establish final dimensions immediately */}
                    {!isTypingComplete && (
                      <div style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                        <MarkdownRenderer content={cleanText} />
                      </div>
                    )}
                    
                    {/* Actual visible typing content */}
                    <div style={!isTypingComplete ? { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' } : {}}>
                      <MarkdownRenderer content={displayedContent} />
                      {!isTypingComplete && <span className="typing-cursor">|</span>}
                    </div>
                  </div>
                </>
              )}
              
              {/* Private Response Banner - show when message is from protected topic */}
              {message.metadata?.protection_status === "protected" && (
                <div className="private-response-banner">
                  <div className="private-response-header">
                    <i className="fas fa-shield-alt"></i>
                    <span className="privacy-badge-text">PRIVATE ANSWER</span>
                  </div>
                  <div className="private-response-description">
                    This query is protected by the privacy system. Only internal knowledge is used.
                  </div>
                </div>
              )}
              
              {/* Debug info for protected topics */}
              {message.metadata?.protection_status === "protected" && (
                <div className="debug-info" style={{fontSize: '0.7rem', color: '#666', marginTop: '0.5rem'}}>
                  Debug: protection_status={message.metadata.protection_status}, 
                  isProtectedTopic={isProtectedTopic}, 
                  isPrivateResponse={isPrivateResponse}
                </div>
              )}
              
            </div>
          </div>
        )}
        
      </div>
      
      {(message.type === 'user' || (!isStreaming && isTypingComplete)) && (
      <div className={`message-meta ${message.type}`}>
        <span className="message-time">{formatTime(message.timestamp)}</span>

        {message.type === 'assistant' && (
          <>
            <div className="message-action-bar">
              {isLLMExtensionAvailable && (
                <div className="message-action-split">
                  <button
                    className={`message-action-button ${contextStatus === 'no' ? 'action-primary' : 'action-ghost'} ${showLLMSelector ? 'active' : ''}`}
                    onClick={handleFooterExtendClick}
                    disabled={isExtending}
                    title="Extend query to LLM for knowledge-based response"
                  >
                    <i className={state.autoModeEnabled ? 'fas fa-magic' : 'fas fa-bolt'}></i>
                    <span>{preferredLLM?.name || preferredLLM?.model ? `Ask ${preferredLLM?.name || preferredLLM?.model}` : 'Consult External Model'}</span>
                  </button>
                  <button
                    className={`message-action-button action-ghost action-split-trigger ${showLLMSelector ? 'active' : ''}`}
                    onClick={handleToggleLLMSelector}
                    disabled={isExtending}
                    title="Choose a different external model"
                    aria-label="Choose a different external model"
                  >
                    <i className={`fas fa-chevron-${showLLMSelector ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              )}

              {!isLLMExtensionAvailable && (message.metadata?.llmProvider || message.metadata?.llmModel) && (
                <span className="message-action-text">
                  <i className="fas fa-bolt"></i>
                  <span>{llmIndicatorLabel}</span>
                </span>
              )}

              {hasSources && (
                <button
                  className={`message-action-button sources-toggle ${!referencesCollapsed ? 'active' : ''}`}
                  onClick={toggleReferencesCollapse}
                  title={referencesCollapsed ? 'Show sources' : 'Hide sources'}
                >
                  <i className="fas fa-book"></i>
                  <span>{message.metadata.contextSources.length} Sources</span>
                </button>
              )}

              {message.metadata?.processingTime && (
                <span className="message-action-text">
                  <i className="far fa-clock"></i>
                  <span>{message.metadata.processingTime.toFixed(1)}s</span>
                </span>
              )}

              {contextStatus === 'yes' && (
                <span className="status-text yes">
                  <i className="fas fa-check-circle"></i>
                  <span>In Context</span>
                </span>
              )}

              {contextStatus === 'no' && (
                <span className="status-text no">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>No Context</span>
                </span>
              )}

              {message.metadata?.protection_status === "protected" && (
                <span className="message-action-text status-protected">
                  <i className="fas fa-shield-alt"></i>
                  <span>Protected</span>
                </span>
              )}

              {message.metadata?.supervisorUsed && (
                <button
                  className={`message-action-button status-supervisor ${!reasoningCollapsed ? 'active' : ''}`}
                  onClick={toggleReasoningPanel}
                  title={reasoningCollapsed ? 'Show thought process' : 'Hide thought process'}
                  disabled={!message.metadata?.agentSteps?.length}
                >
                  <i className="fas fa-brain"></i>
                  <span>Supervisor</span>
                </button>
              )}

              <button
                className="message-action-button"
                onClick={handleCopyResponse}
                title="Copy response"
              >
                <i className="far fa-copy"></i>
                <span>Copy</span>
              </button>
            </div>

            {!reasoningCollapsed && message.metadata?.agentSteps?.length > 0 && (
              <div className="message-reasoning-panel">
                <div className="message-sources-header">
                  <span className="sources-title supervisor-steps-title">Supervisor Steps</span>
                </div>
                <div className="agent-reasoning-stack persistent footer-stack">
                  {message.metadata.agentSteps.map((step, index) => (
                    <div key={index} className="agent-step completed">
                      <span className="step-bullet">›</span>
                      <span>{step.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showLLMSelector && isLLMExtensionAvailable && (
              <div className="message-footer-panel">
                <div className="llm-selector">
                  <div className="llm-options">
                    {state.allowUserModelSelection ? (
                      <>
                        {Array.from(new Set(availableLLMs.map((llm) => llm.id))).map((providerId) => (
                          <div key={providerId} className="llm-provider-group">
                            <div
                              className="provider-header"
                              onClick={() => toggleProviderExpansion(providerId)}
                              role="button"
                              tabIndex={0}
                            >
                              <span>{getProviderDisplayName(providerId)}</span>
                              <i className={`fas fa-chevron-${expandedProviders[providerId] ? 'up' : 'down'} provider-toggle-icon`}></i>
                            </div>
                            {expandedProviders[providerId] && (
                              <div className="provider-models">
                                {availableLLMs
                                  .filter((llm) => llm.id === providerId)
                                  .map((llm, index) => (
                                    <button
                                      key={`${llm.id}-${llm.model}-${index}`}
                                      className="llm-option-btn"
                                      onClick={() => handleSelectLLM(llm)}
                                      title={`Use ${llm.name} (${llm.model})`}
                                    >
                                      <div className="llm-info">
                                        <span className="llm-name">{llm.name}</span>
                                        <span className="llm-model">{llm.model}</span>
                                      </div>
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      availableLLMs.map((llm) => (
                        <button
                          key={llm.id}
                          className="llm-option-btn"
                          onClick={() => handleSelectLLM(llm)}
                          title={`Use ${llm.name} (${llm.model})`}
                        >
                          <div className="llm-info">
                            <span className="llm-name">{llm.name}</span>
                            <span className="llm-model">{llm.model}</span>
                          </div>
                        </button>
                      ))
                    )}
                    {configuredComparisonLLMs.length > 1 && (
                      <button
                        key="comparison-configured-llms"
                        className="llm-option-btn"
                        onClick={() => handleSelectLLM(comparisonOption)}
                        title={`Use comparison with configured agents: ${comparisonOption.llms.map((llm) => (
                          llm.summary ? `summary:${llm.provider}-${llm.model}` : `${llm.provider}:${llm.model}`
                        )).join(', ')}`}
                      >
                        <div className="llm-info">
                          <span className="llm-name">{comparisonOption.name}</span>
                          <span className="llm-model">
                            {comparisonOption.llms
                              .filter((llm) => !llm.summary)
                              .map((llm) => `${llm.name} (${llm.model})`)
                              .join(' + ')}
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                  <button 
                    className="cancel-selector-btn"
                    onClick={() => setShowLLMSelector(false)}
                  >
                    <i className="fas fa-times"></i>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!referencesCollapsed && hasSources && (
              <div className="message-sources-panel">
                <div className="message-sources-header">
                  <span className="sources-title">
                    Sources ({message.metadata.contextSources.length})
                  </span>
                  <div className="sources-breakdown">
                    {contextAnalysis.hasMemorySources && (
                      <span className="sources-badge memory">
                        Memory {contextAnalysis.memorySources.length}
                      </span>
                    )}
                    {contextAnalysis.hasClabsSources && (
                      <span className="sources-badge clabs">
                        Clabs {contextAnalysis.clabsSources.length}
                      </span>
                    )}
                    {contextAnalysis.hasRagSources && (
                      <span className="sources-badge rag">
                        RAG {contextAnalysis.ragSources.length}
                      </span>
                    )}
                  </div>
                </div>
                <div className="context-sources">
                  {[...message.metadata.contextSources]
                    .sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0))
                    .map((source, index) => (
                    <div key={index} className={`context-source ${selectedReference?.memory_id === source.memory_id ? 'expanded' : ''} ${source.provider ? `provider-${source.provider}` : ''}`}>
                      <div 
                        className="context-source-header clickable"
                        onClick={() => handleReferenceClick(source)}
                        title="Click to expand/collapse reference details"
                      >
                        <div className={`provider-indicator ${source.provider || 'memory'}`}></div>
                        <div className="context-source-main">
                          <div className="context-source-top">
                            <span className="context-query">
                              {source.provider === 'rag'
                                ? (source.metadata?.filename || source.metadata?.document_id || source.query || 'RAG document')
                                : `"${source.query}"`}
                            </span>
                            {source.metadata?.url && (
                              <a
                                href={source.metadata.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="context-url-badge"
                                onClick={(e) => e.stopPropagation()}
                                title={source.metadata.title || source.metadata.url}
                              >
                                <i className="fas fa-external-link-alt"></i>
                                {(() => {
                                  try {
                                    return new URL(source.metadata.url).hostname.replace(/^www\./, '');
                                  } catch (e) {
                                    return 'Source Link';
                                  }
                                })()}
                              </a>
                            )}
                            <span className="context-relevance">
                              {(source.relevance_score * 100).toFixed(0)}%
                            </span>
                          </div>
                          {Array.isArray(source.matching_chunks) && source.matching_chunks.length > 0 && (
                            <div className="context-chunks-hint">
                              {source.matching_chunks.length} matching {source.matching_chunks.length === 1 ? 'chunk' : 'chunks'}
                            </div>
                          )}
                          {source.answer_preview && (
                            <div className="context-preview">
                              {source.answer_preview}
                            </div>
                          )}
                        </div>
                        <i className={`fas fa-chevron-${selectedReference?.memory_id === source.memory_id ? 'up' : 'down'} expand-icon`}></i>
                      </div>

                      {selectedReference?.memory_id === source.memory_id && (
                        <div className="context-expanded">
                          <div className="context-expanded-header">
                            <h4>
                              <i className="fas fa-info-circle"></i>
                              Reference Details
                            </h4>
                          </div>
                          {(source.provider === 'rag' || source.source_type === 'rag_document') ? (
                            <div className="context-expanded-content reference-panel-host">
                              <ReferencePanel source={source} />
                              {source.timestamp && (
                                <div className="reference-panel-timestamp">
                                  Created: {formatReferenceTime(source.timestamp)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="context-expanded-content">
                              <div className="context-field">
                                <label>Source ID:</label>
                                <code>{source.memory_id || source.id}</code>
                              </div>
                              <div className="context-field">
                                <label>Provider:</label>
                                <span className={`provider-label ${source.provider}`}>
                                  {getContextProviderLabel(source)}
                                </span>
                              </div>
                              <div className="context-field">
                                <label>Original Query:</label>
                                <p>&quot;{source.query}&quot;</p>
                              </div>
                              <div className="context-field">
                                <label>Relevance Score:</label>
                                <div className="relevance-bar">
                                  <div
                                    className="relevance-fill"
                                    style={{ width: `${(source.relevance_score * 100)}%` }}
                                  ></div>
                                  <span className="relevance-text">
                                    {(source.relevance_score * 100).toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                              {source.timestamp && (
                                <div className="context-field">
                                  <label>Created:</label>
                                  <p>{formatReferenceTime(source.timestamp)}</p>
                                </div>
                              )}
                              {source.provider === 'clabs' && (
                                <>
                                  {source.clabs_config && (
                                    <div className="context-field">
                                      <label>Clabs Configuration:</label>
                                      <p>{source.clabs_config}</p>
                                    </div>
                                  )}
                                  {source.clabs_cluster && (
                                    <div className="context-field">
                                      <label>Cluster:</label>
                                      <p>{source.clabs_cluster}</p>
                                    </div>
                                  )}
                                  {source.metadata?.cscore && (
                                    <div className="context-field">
                                      <label>cScore:</label>
                                      <p>{source.metadata.cscore}%</p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  )
}

export default MessageBubble
